import React, { useEffect, useRef } from 'react';

const ParticleBackground = () => {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let width, height;
    let particles = [];

    // "Effervescence" Configuration
    const config = {
      particleCount: 100,
      color: '168, 85, 247', // Purple base
      glowColor: '59, 130, 246', // Blue accent
    };

    class Particle {
      constructor() {
        this.reset(true);
      }

      reset(initial = false) {
        this.x = Math.random() * width;
        this.y = initial ? Math.random() * height : height + 10;
        this.size = Math.random() * 2 + 0.5; // Tiny stars
        this.speedY = Math.random() * 0.5 + 0.2; // Slow upward drift
        this.speedX = (Math.random() - 0.5) * 0.5; // Slight gentle sway
        this.life = Math.random() * 1; // Opacity cycle
        this.decay = Math.random() * 0.005 + 0.002;
        this.maxSize = this.size + Math.random() * 2; // Max bloom size
      }

      update(mouse) {
        this.y -= this.speedY;
        this.x += this.speedX;
        
        // Twinkle effect (Sine wave opacity)
        this.life += this.decay;
        this.alpha = Math.abs(Math.sin(this.life));

        // Mouse Interaction: "Magical Push"
        const dx = mouse.x - this.x;
        const dy = mouse.y - this.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const influenceRadius = 150;

        if (dist < influenceRadius) {
          const force = (influenceRadius - dist) / influenceRadius;
          const angle = Math.atan2(dy, dx);
          // Gently move away from mouse (playful)
          this.x -= Math.cos(angle) * force * 2;
          this.y -= Math.sin(angle) * force * 2;
          // Bloom when close to mouse (excitement)
          this.currentSize = Math.min(this.maxSize * 1.5, this.size + force * 2); 
        } else {
          this.currentSize = this.size;
        }

        // Reset if off screen
        if (this.y < -10 || this.alpha < 0.01) {
          this.reset();
        }
      }

      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.currentSize, 0, Math.PI * 2);
        
        // Beautiful gradient fill for depth
        const gradient = ctx.createRadialGradient(
          this.x, this.y, 0,
          this.x, this.y, this.currentSize * 2
        );
        gradient.addColorStop(0, `rgba(255, 255, 255, ${this.alpha})`); // White center
        gradient.addColorStop(0.4, `rgba(${config.color}, ${this.alpha * 0.5})`); // Purple mid
        gradient.addColorStop(1, `rgba(${config.glowColor}, 0)`); // Blue fade out
        
        ctx.fillStyle = gradient;
        ctx.fill();
      }
    }

    const init = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
      particles = Array.from({ length: config.particleCount }, () => new Particle());
    };

    const draw = () => {
      // Create a trail effect for "dreamy" feel, but keep background dark
      ctx.fillStyle = 'rgba(10, 10, 10, 0.2)'; // Clears with slight trail
      ctx.fillRect(0, 0, width, height);
      
      // Global Glow
      ctx.globalCompositeOperation = 'screen'; // Makes things shiny

      particles.forEach(p => {
        p.update(mouseRef.current);
        p.draw();
      });

      // Reset composition
      ctx.globalCompositeOperation = 'source-over';

      animationFrameId = requestAnimationFrame(draw);
    };

    const handleResize = () => init();
    const handleMouseMove = (e) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('mousemove', handleMouseMove);
    
    init();
    draw();

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 -z-10 bg-neutral-950"
      style={{ touchAction: 'none' }}
    />
  );
};

export default ParticleBackground;
